import { Bell, Database, FileText, Hash, MapPin, UserCog, Users } from 'lucide-react';
import type { AdminTenantDetailDto } from '../types/tenants.types';

interface ResourceItemProps {
  label: string;
  used: number;
  limit: number;
  icon: React.ReactNode;
}

function ResourceItem({ label, used, limit, icon }: ResourceItemProps) {
  const percentage = limit > 0 ? Math.min((used / limit) * 100, 100) : 0;
  const barColor = percentage >= 90 ? 'bg-negative' : percentage >= 70 ? 'bg-warn' : 'bg-positive';

  return (
    <div className="flex flex-col gap-2 p-4 rounded-xl border border-outline">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-primary">{icon}</span>
          <span className="text-primary text-xs opacity-60 uppercase tracking-wider">{label}</span>
        </div>
        <span className="text-light text-sm font-medium tabular-nums">
          {used}/{limit}
        </span>
      </div>
      <div className="h-2 rounded-full bg-select-background overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${barColor}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

interface TenantResourceUsageProps {
  tenant: AdminTenantDetailDto;
}

export function TenantResourceUsage({ tenant }: TenantResourceUsageProps) {
  const resources = [
    {
      label: 'Miembros',
      used: tenant.memberCount,
      limit: tenant.maxUsers,
      icon: <Users size={14} />,
    },
    {
      label: 'Regiones',
      used: tenant.regionCount,
      limit: tenant.tierMaxRegions,
      icon: <MapPin size={14} />,
    },
    { label: 'Actores', used: 0, limit: tenant.tierMaxActors, icon: <UserCog size={14} /> },
    { label: 'Temas', used: 0, limit: tenant.tierMaxTopics, icon: <Hash size={14} /> },
    { label: 'Alertas', used: 0, limit: tenant.tierMaxAlerts, icon: <Bell size={14} /> },
    { label: 'Fuentes', used: 0, limit: tenant.tierMaxSources, icon: <Database size={14} /> },
    {
      label: 'Reportes',
      used: 0,
      limit: tenant.tierMaxScheduledReports ?? 0,
      icon: <FileText size={14} />,
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {resources.map((resource) => (
        <ResourceItem key={resource.label} {...resource} />
      ))}
    </div>
  );
}
